import { useState } from 'react';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');

  const handleCategorySelect = (url: string, name: string) => {
    setSelectedCategory(name);
    setDropdownOpen(false);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full min-h-[620px] md:min-h-[720px] flex items-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20sage%20green%20studio%20backdrop%20with%20curated%20fashion%20items%20sneakers%20and%20accessories%20arranged%20on%20cool%20marble%20surface%20with%20potted%20monstera%20plant%20natural%20window%20light%20from%20left%20side%20sophisticated%20editorial%20photography%20with%20airy%20botanical%20aesthetic%20muted%20sage%20and%20ivory%20tones&width=1600&height=900&seq=kakobuy-hero-bg-v2&orientation=landscape"
          alt="Kakobuy Spreadsheet fashion finds"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/30"></div>
      </div>

      <div className="relative z-10 w-full px-6 md:px-10 lg:px-16">
        <div className="flex flex-col lg:flex-row items-start gap-10 lg:gap-16 max-w-[1280px] mx-auto">
          <div className="flex-1 pt-16 md:pt-20 lg:pt-24">
            <span className="inline-block text-xs md:text-sm font-medium text-white/80 bg-white/15 backdrop-blur-sm px-3 py-1.5 rounded-full mb-5 tracking-wide uppercase">
              Updated 2026
            </span>

            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4">
              Kakobuy Spreadsheet
              <br />
              <span className="font-light italic opacity-80">The Ultimate Product</span>
              <br />
              <span className="font-light italic opacity-80">Finding Database</span>
            </h1>

            <p className="text-white/80 text-sm md:text-base max-w-lg leading-relaxed mb-6">
              Discover the latest Kakobuy Spreadsheet collections, trending products, verified finds, comparison guides, and shopping resources updated regularly.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-5">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
              >
                Kakobuy Sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </span>
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>

            <div className="relative max-w-[280px]">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="w-full flex items-center justify-between gap-2 bg-white/15 backdrop-blur-sm border border-white/30 text-white px-4 py-3 rounded-xl text-sm font-medium hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                <span>{selectedCategory || 'Select a Category'}</span>
                <span className={`w-5 h-5 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-base"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.12)] border border-background-200 py-2 z-20 max-h-[320px] overflow-y-auto">
                  {categories.map((cat) => (
                    <button
                      key={cat.name}
                      onClick={() => handleCategorySelect(cat.url, cat.name)}
                      className="w-full text-left px-4 py-2.5 text-sm text-foreground-800 hover:bg-background-100 transition-colors flex items-center gap-2.5 cursor-pointer whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-foreground-500">
                        <i className={`${cat.icon} text-sm`}></i>
                      </span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-3 mt-5">
              <span className="text-white/60 text-xs flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-accent-400 text-sm"></i>
                </span>
                Updated Product Lists
              </span>
              <span className="text-white/60 text-xs flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-accent-400 text-sm"></i>
                </span>
                Community Recommended
              </span>
              <span className="text-white/60 text-xs flex items-center gap-1">
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-check-line text-accent-400 text-sm"></i>
                </span>
                Detailed Guides
              </span>
            </div>
          </div>

          <div className="hidden lg:flex flex-1 items-center justify-end pt-20">
            <div className="relative w-[420px] h-[480px]">
              <div className="absolute right-0 top-0 w-[300px] h-[420px] rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.15)]">
                <img
                  src="https://readdy.ai/api/search-image?query=Premium%20leather%20handbag%20and%20silk%20scarf%20draped%20on%20rich%20terracotta%20fabric%20backdrop%20dramatic%20natural%20window%20light%20casting%20deep%20shadows%20warm%20amber%20and%20rust%20tones%20luxury%20editorial%20product%20photography%20with%20Mediterranean%20villa%20aesthetic&width=600&height=840&seq=kakobuy-collage-main-v2&orientation=portrait"
                  alt="Kakobuy product finds"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute left-0 top-12 w-[180px] h-[180px] rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.12)] border-4 border-white">
                <img
                  src="https://readdy.ai/api/search-image?query=Designer%20sunglasses%20and%20delicate%20gold%20jewelry%20arranged%20on%20pure%20white%20marble%20surface%20with%20soft%20grey%20veining%20bright%20natural%20daylight%20from%20overhead%20skylight%20clean%20luxury%20aesthetic%20with%20crisp%20cool%20tones%20and%20sharp%20reflections%20editorial%20style&width=360&height=360&seq=kakobuy-collage-top-v2&orientation=squarish"
                  alt="Kakobuy hoodie finds"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute left-10 bottom-8 w-[160px] h-[200px] rounded-xl overflow-hidden shadow-[0_8px_24px_rgba(0,0,0,0.1)] border-4 border-white">
                <img
                  src="https://readdy.ai/api/search-image?query=Designer%20sneakers%20on%20textured%20concrete%20studio%20floor%20with%20dramatic%20angular%20shadows%20urban%20minimalist%20aesthetic%20cool%20grey%20and%20slate%20tones%20architectural%20photography%20style%20with%20geometric%20shadow%20patterns%20and%20industrial%20edge&width=320&height=400&seq=kakobuy-collage-bottom-v2&orientation=portrait"
                  alt="Kakobuy accessories"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}